import React, { FC, Suspense, useContext, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import classNames from "classnames";
import { Badge, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RouteComponentProps } from "react-router";
import { Link, useHistory, useLocation } from "react-router-dom";
import { wrapPromise } from "go-core";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import FormatBoolean from "go-core/components/Formatters/FormatBoolean";
import FormatDate, { FormatDateToDateHoursRange, defaultFormatDate } from "go-core/components/Formatters/FormatDate";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { LoadingContainer } from "go-core/components/Loading";
import RenderLimitedText from "go-core/components/RenderLimitedText";
import { StickyColumnWithEntityStatus } from "go-core/components/StickyColumnWithEntityStatus";
import { useWindowSize } from "go-core/components/useWindowSize";
import { ReactComponent as AlertSVG } from "go-core/images/svg/alert.svg";
import { isAndroidWebView } from "go-core/utils/utils";
import RenderColumnUtils from "go-list/core/components/Actions/services/RenderColumnUtils";
import { ListFilterSource } from "go-list/core/components/Filter/services/types";
import EntityStatus from "go-list/core/components/Table/components/EntityStatus";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig, ListParamsType, MultipleActionsParams } from "go-list/list/services/types";
import { hasPermission, selectOrganization } from "go-security/services/organizations/selectors";
import { selectIsAdmin } from "go-security/services/users/selectors";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { apiOrganization } from "../../../../../../../../../services/Api/Organization/apiOrganization";
import {
	InvoiceApi,
	KSeFInvoiceApi,
	KSeFInvoiceApiStatus,
	LinkedInvoiceApi,
} from "../../../../../../../../../services/Api/Organization/types";
import { convertListStatusToEntityStatus } from "../../../../../../../../../utils/entityStatus/entityStatus";
import ExportInvoiceConfirmationModal from "../../../components/ExportInvoicesConfirmationModal/ExportInvoiceConfirmationModal";
import SendInvoiceByEmailModal from "../../../components/SendInvoiceByEmailModal/SendInvoiceByEmailModal";
import { exportEInvoice, exportInvoicePdf, exportInvoicesPdf, printInvoicePdf } from "../../../services/exportInvoice";
import { getInvoiceDocumentTypeAbbreviation } from "../../../utils";

interface ListState {
	resource?: Record<string, any>;
	mobileActions: MobileActionProps[];
}

const listName = "INVOICE";
const resourceType = "INVOICE";

const List: FC<ListState> = ({ resource, mobileActions }) => {
	const history = useHistory();
	const location = useLocation();
	const { t } = useTranslation();
	const [params, setParams] = useState<Record<string, any>>({});
	const organization = useSelector(selectOrganization);
	const [items, setItems] = useState<InvoiceApi[]>([]);
	const [showSendInvoiceByEmailModal, setShowSendInvoiceByEmailModal] = useState(false);
	const [invoiceToSendByEmail, setInvoiceToSendByEmail] = useState<InvoiceApi | undefined>(undefined);
	const segmentContext = useContext(SegmentContext);
	const hasVenueInvoiceEditPermission = useSelector(
		hasPermission(["VENUE_OWN_INVOICE_MANAGE", "VENUE_INVOICE_EDIT"])
	);
	const isAdmin = useSelector(selectIsAdmin);
	const [invoicesToExportToPdf, setInvoicesToExportToPdf] = useState<InvoiceApi[] | undefined>(undefined);
	const { addSuccessFlash } = useFlash();

	if (!resource) return null;
	const data = resource.read();

	const onRefresh = async () => {
		if (config.fetch) {
			const res = await config.fetch(params);
			setItems(res);
		}
	};

	const getPreviewLink = (item: InvoiceApi) => {
		if (item.document_type === "INVOICE") {
			return `${location.pathname}/${item.id}`;
		}
		if (item.document_type === "CORRECTION") {
			return `${location.pathname}/${
				item.linked_invoices.find((i) => i.document_type === "INVOICE" || i.document_type === "ADVANCE")?.id
			}/corrections?correction_id=${item.id}`;
		}
		if (item.document_type === "ADVANCE") {
			return `${location.pathname}/advances/${item.id}`;
		}

		return "";
	};

	const removeInvoice = async (invoice: InvoiceApi) => {
		await apiOrganization.removeInvoice(invoice.id);
		addSuccessFlash(t("common.flash.removed", { ns: "lib" }));
	};

	const hasAtLeastOneIncompatibleTaxIdNo = (invoice: InvoiceApi) => {
		if (invoice.orders.length > 0) return invoice.orders.some((order) => order?.tax_id_no_mismatch);
	};

	const KSeFInvoiceHasError = (invoice: InvoiceApi) => invoice.ksef_data?.status === KSeFInvoiceApiStatus.ERROR;

	const exportInvoiceToPdf = (invoice: InvoiceApi) => {
		if (invoice.status === "OPEN") {
			setInvoicesToExportToPdf([invoice]);
		} else {
			exportInvoicePdf(invoice, t);
		}
	};

	const exportInvoicesToPdf = (invoices: InvoiceApi[]) => {
		const invoiceWithOpenStatus = invoices.find((invoice) => invoice.status === "OPEN");
		if (invoiceWithOpenStatus) {
			setInvoicesToExportToPdf(invoices);
		} else {
			exportInvoicesPdf(invoices, t, params);
		}
	};

	const getLinkToLinkedInvoice = (invoice: InvoiceApi, linkedInvoice: LinkedInvoiceApi) => {
		switch (linkedInvoice.document_type) {
			case "PROFORMA":
				return `/${organization.id}/invoices/proforma/${linkedInvoice.id}`;
			case "INVOICE":
				return `/${organization.id}/invoices/${linkedInvoice.id}`;
			case "ADVANCE":
				return `/${organization.id}/invoices/advances/${linkedInvoice.id}`;
			case "CORRECTION":
				return `/${organization.id}/invoices/${invoice.id}/corrections`;
			default:
				return `/${organization.id}/invoices`;
		}
	};

	const sendToKSeFHandler = async (invoiceId: number) => {
		await apiOrganization.sendInvoiceToKSeF(invoiceId);
		addSuccessFlash(t("lib:common.flash.completed"));
	};

	const getKSeFInvoiceDetails = (KSeFData: KSeFInvoiceApi) => {
		if (KSeFData.status === KSeFInvoiceApiStatus.ERROR) {
			return t("modules.invoice.field.missing_invoice_in_KSeF_system.title");
		} else if (KSeFData.status === KSeFInvoiceApiStatus.PENDING) {
			return t("modules.invoice.action.sending_to_ksef_in_progress.title");
		}

		return KSeFData.number;
	};

	const newCorrectionToZeroHandler = async (invoiceId: number) => {
		await apiOrganization.createInvoiceCorrectionToZero(invoiceId);
		addSuccessFlash(t("lib:common.flash.completed"));
	};

	const displayInvoiceErrorTooltip = (item: InvoiceApi) =>
		hasAtLeastOneIncompatibleTaxIdNo(item) || KSeFInvoiceHasError(item);

	const sendMultipleInvoicesToKSeF = async (
		multipleActionsItems: InvoiceApi[],
		_?: ListParamsType,
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => {
		const idsAsString = multipleActionsItems.map((item) => item.id.toString()).join(",");
		await apiOrganization.sendInvoicesToKSeF(idsAsString, multipleActionParams, areAllItemsSelected);
		addSuccessFlash(t("lib:common.flash.completed"));
	};

	let config = {
		fields: [
			{
				id: "issued_at",
				name: t("modules.invoice.field.date_verb.title"),
				type: "date",
				render: (item: InvoiceApi) => {
					return (
						<div className="d-flex align-items-center column-gap-1">
							{displayInvoiceErrorTooltip(item) && (
								<OverlayTrigger
									overlay={
										<Tooltip id={`tooltip-${item.id}`}>
											{hasAtLeastOneIncompatibleTaxIdNo(item) && (
												<span className="d-inline-block">
													{t("modules.invoice.constraints.incompatible_tax_id_no")}
												</span>
											)}
											{KSeFInvoiceHasError(item) && (
												<span className="d-inline-block">
													{t("modules.invoice.field.missing_invoice_in_KSeF_system.title")}
												</span>
											)}
										</Tooltip>
									}
								>
									{({ ref, ...triggerHandler }) => <AlertSVG ref={ref} {...triggerHandler} />}
								</OverlayTrigger>
							)}
							<StickyColumnWithEntityStatus
								status={convertListStatusToEntityStatus(
									item.status === "OPEN" ? "OPEN_BLUE" : item.status
								)}
								to={getPreviewLink(item)}
							>
								{FormatDate(item.dates?.issued_at, undefined, false, defaultFormatDate)}
							</StickyColumnWithEntityStatus>
						</div>
					);
				},
				renderExport: (item: InvoiceApi) => {
					return FormatDate(item.dates?.issued_at, undefined, false, defaultFormatDate);
				},
			},
			{
				id: "document",
				name: t("modules.invoice.field.document.title"),
				render: (item: InvoiceApi) => <Link to={getPreviewLink(item)}>{item.number}</Link>,
				renderExport: (item: InvoiceApi) => item.number,
			},
			{
				id: "document_type",
				name: t("modules.invoice.field.document_type.title"),
				render: (item: InvoiceApi) => getInvoiceDocumentTypeAbbreviation(item),
				disableSorting: true,
			},
			{
				id: "ksef_data",
				name: t("modules.invoice.header.ksef_info.title"),
				render: (item: InvoiceApi) => {
					const { ksef_data } = item;

					if (!ksef_data) return t("modules.invoice.action.not_sent_to_ksef.title");

					const { status, online, environment_type, verification_qr_code_url } = ksef_data;

					const KSeFDetails = getKSeFInvoiceDetails(ksef_data);

					return (
						<div className="d-flex align-items-center">
							<EntityStatus status={convertListStatusToEntityStatus(status)} />
							{!online && (
								<Badge bg="danger" className="me-1">
									Offline
								</Badge>
							)}
							<span className={classNames({ "text-danger": status === KSeFInvoiceApiStatus.ERROR })}>
								{verification_qr_code_url ? (
									<Link
										to={{ pathname: verification_qr_code_url }}
										target="_blank"
										rel="noreferrer noopener"
									>
										{KSeFDetails}
									</Link>
								) : (
									KSeFDetails
								)}
							</span>
							{environment_type === "TEST" && (
								<Badge bg="warning" text="dark" className="ms-2">
									Test
								</Badge>
							)}
						</div>
					);
				},
				renderExport: (item: InvoiceApi) => {
					if (!item.ksef_data) return t("modules.invoice.action.not_sent_to_ksef.title");

					const { number, online } = item.ksef_data;
					let invoiceAdditionalData = number ?? "";

					if (number) return number;

					if (!online) invoiceAdditionalData += `${invoiceAdditionalData ? " " : ""}(Offline)`;

					return `${t(
						`enums.invoices.KSeF_export_status.${item.ksef_data.status}`
					)} ${invoiceAdditionalData}`;
				},
				disableSorting: true,
			},
			{
				id: "client",
				name: t("modules.invoice.field.recipient.title"),
				render: (item: InvoiceApi) => {
					if (item.recipient && item.recipient_id) {
						return (
							<div className="d-flex flex-column">
								<RenderLimitedText minWidth={200}>
									<Link
										rel={"noreferrer"}
										target={"_blank"}
										to={`/${organization.id}/clients/${item.recipient_id}`}
									>
										{item.recipient.name}
									</Link>
								</RenderLimitedText>
							</div>
						);
					}
				},
				renderExport: (item: InvoiceApi) => {
					return item.recipient.name;
				},
			},
			{
				id: "recipient_client_group",
				name: t("modules.invoice.field.recipient_client_group_name.title"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						apiOrganization.getClientGroupsSearchSelect(search, params, {
							cancelToken: options?.token,
						}),
				},
				render: (item: InvoiceApi) => item.recipient?.client_group_name ?? "",
			},
			{
				id: "tax_id_no",
				name: t("common.word.tax_id_no.title", { ns: "lib" }),
				render: (item: InvoiceApi) => {
					return (
						<div className="d-flex align-items-center">
							<span>{item.recipient.tax_id_no}</span>
							{hasAtLeastOneIncompatibleTaxIdNo(item) && (
								<OverlayTrigger
									placement="top"
									overlay={
										<Tooltip id={`tooltip-${item.id}`}>
											{t("modules.invoice.constraints.incompatible_tax_id_no")}
										</Tooltip>
									}
								>
									{({ ref, ...triggerHandler }) => (
										<AlertSVG className={"ms-1"} ref={ref} {...triggerHandler} />
									)}
								</OverlayTrigger>
							)}
						</div>
					);
				},
				renderExport: (item: InvoiceApi) => item.recipient.tax_id_no,
			},
			{
				id: "orders",
				name: t("modules.invoice.field.orders.title"),
				render: (item: InvoiceApi) => {
					return (
						<div className="d-flex flex-column">
							{item.orders?.map((order) =>
								order?.order_id ? (
									<Link
										key={`order_${order.order_id}`}
										rel={"noreferrer"}
										target={"_blank"}
										to={`/${organization.id}/sales/orders/${order.order_id}`}
									>
										{order.number}
									</Link>
								) : (
									<></>
								)
							)}
						</div>
					);
				},
				renderExport: (item: InvoiceApi) => {
					return item.orders?.map((order) => order?.number).join(", ");
				},
				disableSorting: true,
			},
			{
				id: "linked_invoices",
				name: t("modules.invoice.field.linked_invoices.title"),
				render: (item: InvoiceApi) => {
					if (Array.isArray(item.linked_invoices)) {
						return item.linked_invoices.map((linked_invoice) => {
							return (
								<Link
									key={`linked_invoice_${linked_invoice.id}`}
									to={getLinkToLinkedInvoice(item, linked_invoice)}
								>
									{`${t(`enums.invoices.document_type.${linked_invoice.document_type}`)} ${
										linked_invoice.number
									}`}
									<br />
								</Link>
							);
						});
					}
					return "";
				},
				renderExport: (item: InvoiceApi) => {
					return item.linked_invoices
						.map((linked_invoice) => {
							return `${linked_invoice.number}`;
						})
						.join(",\n");
				},
				disableSorting: true,
			},
			{
				id: "price_sum_net",
				name: t("common.word.amount_net"),
				render: (item: InvoiceApi) => {
					return FormatMoney(item.summary?.price_sum_net);
				},
				styleOverride: RenderColumnUtils.getMoneyStyles(),
			},
			{
				id: "price_sum_gross",
				name: t("common.word.amount_gross"),
				render: (item: InvoiceApi) => {
					return FormatMoney(item.summary?.price_sum_gross);
				},
				styleOverride: RenderColumnUtils.getMoneyStyles(),
			},
			{
				id: "sold_at",
				name: t("modules.invoice.field.sold_at.title"),
				type: "date",
				render: (item: InvoiceApi) => {
					return FormatDate(item.dates?.sold_at, undefined, false, defaultFormatDate);
				},
			},
			{
				id: "payment_due_date",
				name: t("modules.invoice.field.payment_due_date.title"),
				type: "date",
				render: (item: InvoiceApi) => {
					return FormatDate(item.dates?.payment_due_date, undefined, false, defaultFormatDate);
				},
			},
			{
				id: "payment_method",
				name: t("common.word.payment_method"),
				disableSorting: true,
				render: (item: InvoiceApi) =>
					item.payments?.map((payment) => payment?.transaction?.payment_method_name).join(", "),
			},
			{
				id: "paid",
				name: t("modules.invoice.field.paid.list.title"),
				type: "boolean",
				render: (item: InvoiceApi) => <FormatBoolean value={item.paid} />,
				renderExport: (item: InvoiceApi) => {
					return `${item.paid ? t("modules.invoice.field.paid.csv.title") : t("lib:common.word.no")}`;
				},
			},
			{
				id: "advances_to_invoice",
				name: t("modules.invoice.field.advances_to_invoice.title"),
				render: (item: InvoiceApi) => {
					const advances = item.linked_invoices.filter(
						(linkedInvoice) => linkedInvoice.document_type === "ADVANCE"
					);

					if (advances.length === 0) return "";

					return advances.map((advance) => {
						return (
							<Link to={`/${organization.id}/invoices/advances/${advance.id}`} key={advance.id}>
								{advance.number}
							</Link>
						);
					});
				},
				renderExport: (item: InvoiceApi) =>
					item.linked_invoices
						.filter((invoice) => invoice.document_type === "ADVANCE")
						.map((advance) => advance.number)
						.join(", "),
				disableSorting: true,
			},
			{
				id: "corrections_to_invoice",
				name: t("modules.invoice.field.corrections_to_invoice.title"),
				render: (item: InvoiceApi) => {
					const corrections = item.linked_invoices.filter(
						(linkedInvoice) => linkedInvoice.document_type === "CORRECTION"
					);

					if (corrections.length === 0) return "";

					return corrections.map((correction) => {
						return (
							<Link to={`/${organization.id}/invoices/${item.id}/corrections`} key={correction.id}>
								{correction.number}
							</Link>
						);
					});
				},
				renderExport: (item: InvoiceApi) =>
					item.linked_invoices
						.filter((invoice) => invoice.document_type === "CORRECTION")
						.map((correction) => correction.number)
						.join(", "),
				disableSorting: true,
			},
		],
		filters: [
			{
				id: "client",
				name: t("modules.invoice.field.recipient.title"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						apiOrganization.getClientsSearchSelect(search, params, {
							cancelToken: options?.token,
						}),
				},
				hasDefaultOptions: false,
			},
			{
				id: "payment_method",
				name: t("common.word.payment_method"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						apiOrganization.getPaymentMethodsSearchSelect(search, params, {
							cancelToken: options?.token,
						}),
				},
				hasDefaultOptions: false,
			},
			{
				type: "list",
				name: t("modules.invoice.field.document_type.title"),
				id: "document_type",
				options: {
					INVOICE: t("enums.invoices.document_type.INVOICE"),
					CORRECTION: t("enums.invoices.document_type.CORRECTION"),
					ADVANCE: t("enums.invoices.document_type.ADVANCE"),
				},
				hasDefaultOptions: false,
			},
			{
				id: "price",
				name: t("common.word.amount_gross"),
				type: "number",
			},
			{
				id: "order",
				name: t("common.word.order"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						apiOrganization.getOrdersSearchSelect(search, params, {
							cancelToken: options?.token,
						}),
					render: (item: InvoiceApi) => {
						if (item.created_at && item.closed_at) {
							return `#${item.label} (${FormatDateToDateHoursRange(item.created_at, item.closed_at)})`;
						}
						return `#${item.label} (${FormatDate(
							item.created_at || "",
							undefined,
							false,
							defaultFormatDate
						)})`;
					},
				} as ListFilterSource,
			},
			{
				id: "tax_id_no_mismatch",
				name: t("modules.invoice.constraints.tax_id_no_mismatch"),
				type: "boolean",
			},
			{
				id: "report",
				name: t("common.word.pos_report"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						apiOrganization.getPosReportsSearchSelect(search, params, {
							cancelToken: options?.token,
						}),
					render: (item: InvoiceApi) => {
						if (item.opened_at && item.closed_at) {
							return `${t(`enums.pos_reports.type.${item.type}`)} #${
								item.label
							} (${FormatDateToDateHoursRange(item.opened_at, item.closed_at)})`;
						}
						return `${t(`enums.pos_reports.type.${item.type}`)} #${item.label} (${FormatDate(
							item.opened_at || "",
							undefined,
							false,
							defaultFormatDate
						)})`;
					},
				} as ListFilterSource,
			},
			{
				id: "status",
				name: t("modules.invoice.field.invoice_status.title"),
				type: "list",
				listOptions: {
					OPEN: t(`enums.common.status.OPEN`),
					CONFIRMED: t(`enums.common.status.CONFIRMED`),
					REMOVED: t(`enums.common.status.REMOVED`),
				},
			},
			{
				id: "ksef_number",
				name: t("modules.invoice.field.ksef_number.title"),
				type: "text",
			},
			{
				id: "ksef_status",
				name: t("modules.invoice.field.ksef_status.title"),
				type: "list",
				listOptions: {
					SUCCESS: t("enums.invoices.KSeF_export_status.SUCCESS"),
					PENDING: t("enums.invoices.KSeF_export_status.PENDING"),
					ERROR: t("enums.invoices.KSeF_export_status.ERROR"),
				},
			},
			{
				id: "ksef_issue_mode",
				name: t("modules.invoice.field.ksef_issue_mode.title"),
				type: "list",
				listOptions: {
					ONLINE: "Online",
					OFFLINE: "Offline",
				},
			},
			{
				id: "ksef_missing",
				name: t("modules.invoice.field.ksef_missing.title"),
				type: "boolean",
			},
			{
				id: "ksef_environment_type",
				name: t("modules.invoice.field.ksef_environment_type.title"),
				type: "list",
				listOptions: {
					TEST: t("enums.invoices.KSeF_environment.TEST"),
					PROD: t("enums.invoices.KSeF_environment.PROD"),
				},
				hasDefaultOptions: false,
			},
		],
		actions: [
			{
				name: t("common.action.preview", { ns: "lib" }),
				link: (item: InvoiceApi) => {
					if (item.document_type === "INVOICE") {
						return `${location.pathname}/${item.id}`;
					}
					if (item.document_type === "CORRECTION") {
						return `${location.pathname}/${
							item.linked_invoices.find(
								(i) => i.document_type === "INVOICE" || i.document_type === "ADVANCE"
							)?.id
						}/corrections?correction_id=${item.id}`;
					}
					if (item.document_type === "ADVANCE") {
						return `${location.pathname}/advances/${item.id}`;
					}
					if (item.document_type === "PROFORMA") {
						return `/${location.pathname}/invoices/proforma/${item.id}`;
					}

					return location.pathname;
				},
			},
			{
				name: t("common.action.edit", { ns: "lib" }),
				link: (item: InvoiceApi) => {
					if (item.document_type === "INVOICE") {
						return `${location.pathname}/${item.id}/edit`;
					}
					if (item.document_type === "CORRECTION") {
						return `${location.pathname}/${
							item.linked_invoices.find(
								(i) => i.document_type === "INVOICE" || i.document_type === "ADVANCE"
							)?.id
						}/corrections/edit?correction_id=${item.id}`;
					}
					if (item.document_type === "ADVANCE") {
						return `${location.pathname}/advances/${item.id}/edit`;
					}
					if (item.document_type === "PROFORMA") {
						return `/${location.pathname}/invoices/proforma/${item.id}/edit`;
					}

					return location.pathname;
				},
				visible: (item: InvoiceApi) =>
					!(!hasVenueInvoiceEditPermission && item.status === "CONFIRMED" && !isAdmin),
			},
			{
				name: t("common.action.export_pdf", { ns: "lib" }),
				click: (item: InvoiceApi) => exportInvoiceToPdf(item),
				visible: (_: InvoiceApi) => !isAndroidWebView,
			},
			{
				name: t("common.action.print", { ns: "lib" }),
				click: (item: InvoiceApi) => {
					printInvoicePdf(item);
				},
				visible: (_: InvoiceApi) => !isAndroidWebView,
			},
			{
				name: t("modules.invoice.field.e_invoice.title"),
				click: (item: InvoiceApi) => {
					exportEInvoice(item, t);
				},
				visible: (item: InvoiceApi) => item.document_type !== "ADVANCE" && !isAndroidWebView,
			},
			{
				name: t("common.action.remove", { ns: "lib" }),
				click: (item: InvoiceApi) => removeInvoice(item),
				refreshListAfter: true,
				confirmation: { use: true, message: t("confirmation.message.remove", { ns: "lib" }) },
			},
			{
				name: t("modules.invoice.action.send_invoice_by_e-mail.title"),
				click: (item: InvoiceApi) => {
					setShowSendInvoiceByEmailModal(true);
					setInvoiceToSendByEmail(item);
				},
			},
			{
				name: t("modules.invoice.action.make_correction.title"),
				link: (item: InvoiceApi) => `${location.pathname}/${item.id}/corrections/new`,
				visible: (item: InvoiceApi) =>
					(item.document_type === "INVOICE" || item.document_type === "ADVANCE") &&
					item.linked_invoices.every((linkedInvoice) => linkedInvoice.document_type !== "CORRECTION"),
			},
			{
				name: t("modules.invoice.action.new_correction_to_zero.title"),
				click: (item: InvoiceApi) => newCorrectionToZeroHandler(item.id),
				confirmation: { use: true },
				refreshListAfter: true,
				visible: (item: InvoiceApi) =>
					(item.document_type === "INVOICE" || item.document_type === "ADVANCE") &&
					item.linked_invoices.every((linkedInvoice) => linkedInvoice.document_type !== "CORRECTION"),
			},
			{
				name: t("modules.invoice.action.send_to_ksef.title"),
				click: (item: InvoiceApi) => sendToKSeFHandler(item.id),
				refreshListAfter: true,
				confirmation: { use: true },
				visible: (item: InvoiceApi) =>
					(!item.ksef_data ||
						(Boolean(item.ksef_data) && item.ksef_data.status === KSeFInvoiceApiStatus.ERROR)) &&
					(item.document_type === "INVOICE" ||
						item.document_type === "CORRECTION" ||
						item.document_type === "ADVANCE"),
			},
		],
		multipleActions: [
			{
				name: t("common.action.export_pdf", { ns: "lib" }),
				click: (multipleActionsItems: InvoiceApi[]) => exportInvoicesToPdf(multipleActionsItems),
				visible: (_: InvoiceApi) => !isAndroidWebView,
			},
			{
				name: t("modules.invoice.action.send_to_ksef.title"),
				click: async (
					multipleActionsItems: InvoiceApi[],
					listParams?: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionParams?: MultipleActionsParams
				) =>
					sendMultipleInvoicesToKSeF(
						multipleActionsItems,
						listParams,
						areAllItemsSelected,
						multipleActionParams
					),
				visible: (multipleActionsItems: InvoiceApi[]) => {
					return (
						multipleActionsItems.filter(
							(actionItem) =>
								(!actionItem.ksef_data ||
									actionItem.ksef_data?.status === KSeFInvoiceApiStatus.ERROR ||
									(!actionItem.ksef_data?.online &&
										actionItem.ksef_data?.status !== KSeFInvoiceApiStatus.SUCCESS)) &&
								(actionItem.settings.ksef_token_exists || actionItem.settings.ksef_auth_data_exists)
						).length > 0
					);
				},
				refreshListAfter: true,
				confirmation: { use: true },
			},
		],
		customPageSizes: ["20", "100", "1000"],
		selectedColumns: [
			"issued_at",
			"document",
			"ksef_data",
			"client",
			"recipient_client_group",
			"tax_id_no",
			"orders",
			"linked_invoices",
			"price_sum_net",
			"price_sum_gross",
			"sold_at",
			"payment_due_date",
			"payment_method",
			"paid",
		],
		segments: [
			{
				id: "all",
				name: t("common.word.all", { ns: "lib" }),
				slug: "all",
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		exportConfig: {
			title: t("modules.invoice.field.export_config.title"),
			filename: t("modules.invoice.header.title"),
			organization: `${organization.name}`,
			taxIdNo: organization?.more?.print_company_on_pdf ? organization.more?.company_tax_id_no : undefined,
			company: organization?.more?.print_company_on_pdf ? organization.more?.company_name : undefined,
			pdfOrientation: organization?.more?.pdf_orientation,
			pdfFontSize: organization?.more?.default_pdf_font_size?.toString(),
		},
		fetch: (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			params.include =
				"summary,recipient,receiver,linked_invoices,orders,payments,payments.transaction,custom_fields,recipient.client_group,ksef,details";
			setParams(params);
			return apiOrganization.getInvoices(params, { cancelToken: sourceToken?.token });
		},
		saveSegment: (segment: SegmentType) => {
			return segmentContext.save(listName, resourceType, segment);
		},
		doesIdColumnRedirectToPreviewPage: true,
		numberOfStickyColumnsAtTheStart: 2,
		fetchTotalItemsCountForSelectedFilters: (params: Record<string, any>, sourceToken?: CancelTokenSource) =>
			apiOrganization.getInvoicesCount(params, { cancelToken: sourceToken?.token }),
	} as ListConfig;
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		customFields: data.fields,
		filterValues: data.filter_values,
	};

	return (
		<>
			{invoiceToSendByEmail && (
				<SendInvoiceByEmailModal
					isShown={showSendInvoiceByEmailModal}
					onHide={() => {
						setShowSendInvoiceByEmailModal(false);
						setInvoiceToSendByEmail(undefined);
					}}
					invoice={invoiceToSendByEmail}
					onSuccess={onRefresh}
				/>
			)}
			{invoicesToExportToPdf && (
				<ExportInvoiceConfirmationModal
					isShown={!!setInvoicesToExportToPdf}
					onHide={() => setInvoicesToExportToPdf(undefined)}
					onSuccessAction={onRefresh}
					params={params}
					invoices={invoicesToExportToPdf}
				/>
			)}
			<ListData
				data={items}
				config={config}
				emptyList={{ addAction: () => history.push(`${location.pathname}/new`) }}
				onFetch={(fetchItems: InvoiceApi[]) => {
					setItems(fetchItems);
				}}
				canManageMultiActionsForAllItems
				mobileActions={mobileActions}
			/>
		</>
	);
};

const OrganizationInvoicesIndexIndexPage: FC<RouteComponentProps> = (props) => {
	const { t } = useTranslation();
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContext = useContext(SegmentContext);
	const customFieldResourceTypes = [
		{
			type: "INVOICE",
			name: t(`enums.custom_fields.resources.INVOICE`),
		},
	];
	const isMobile = useWindowSize().isMobile;
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle(t("modules.invoice.header.title"));
		setResource(wrapPromise(segmentContext.get(listName, resourceType, customFieldResourceTypes)));
	}, []);

	const buttons: ButtonProps[] = [
		{
			path: `${props.match.url}/new`,
			variant: "primary",
			title: t("common.word.invoice"),
		},
		{
			path: `${props.match.url}/advances/new`,
			variant: "primary",
			title: t("modules.invoice.action.make_advance.title"),
		},
		{
			path: `${props.match.url}/new_to_order`,
			variant: "primary",
			title: t("modules.invoice.action.create_invoice_to_order.title"),
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			path: `${props.match.url}/new`,
			title: t("common.word.invoice"),
		},
		{
			path: `${props.match.url}/advances/new`,
			title: t("modules.invoice.action.make_advance.title"),
		},
		{
			path: `${props.match.url}/new_to_order`,
			title: t("modules.invoice.action.create_invoice_to_order.title"),
		},
	];

	return (
		<>
			{!isMobile && <Header title={t("modules.invoice.header.title")} buttons={buttons} />}
			<Suspense fallback={<LoadingContainer />}>
				<List resource={resource} mobileActions={mobileActions} />
			</Suspense>
		</>
	);
};
export default OrganizationInvoicesIndexIndexPage;

// t("enums.invoices.KSeF_export_status.ERROR")
// t("enums.invoices.KSeF_export_status.SUCCESS")
// t("enums.invoices.KSeF_export_status.PENDING")
